import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Home, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { runGeneration } from "@/lib/generate";
import { GeneratingOverlay } from "@/components/GeneratingOverlay";
import { useLang } from "@/context/LanguageContext";
import { Header } from "@/components/Header";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AGE_GROUPS, LANGUAGES, THEMES, label } from "@/lib/activityData";

export default function FamilyConnection() {
  const { t, lang } = useLang();
  const navigate = useNavigate();
  const [ageGroup, setAgeGroup] = useState("");
  const [theme, setTheme] = useState("");
  const [language, setLanguage] = useState("bilingual");
  const [loading, setLoading] = useState(false);
  const [elapsed, setElapsed] = useState(0);

  const submit = async () => {
    if (!ageGroup || !theme) { toast.error(lang === "es" ? "Elige grupo de edad y tema." : "Choose an age group and theme."); return; }
    setLoading(true); setElapsed(0);
    try {
      const j = await runGeneration("family", { age_group: ageGroup, theme, language }, { onTick: setElapsed });
      toast.success(t("activity.saved"));
      navigate(`/activities/${j.result_id}`);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || err.message); }
    finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-background">
      <GeneratingOverlay open={loading} elapsed={elapsed} />
      <Header />
      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-8 space-y-8">
        <button onClick={() => navigate("/dashboard")} className="flex items-center gap-2 text-muted-foreground hover:text-foreground text-sm font-semibold" data-testid="back-btn">
          <ArrowLeft className="h-4 w-4" /> {t("nav.dashboard")}
        </button>
        <div className="space-y-2">
          <h1 className="font-heading font-extrabold text-3xl tracking-tight">{lang === "es" ? "Generador de Conexión Familiar" : "Family Connection Generator"}</h1>
          <p className="text-muted-foreground text-lg">{lang === "es" ? "Actividades simples y respetuosas para aprender en casa — Aprendamos Juntos." : "Simple, respectful home-learning activities — Let's Learn Together."}</p>
        </div>
        <div className="space-y-6 rounded-3xl border border-border bg-card p-6 sm:p-8">
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>{t("generator.ageGroup")} <span className="text-secondary">*</span></Label>
              <Select value={ageGroup} onValueChange={setAgeGroup}>
                <SelectTrigger className="h-12 rounded-xl" data-testid="fam-age"><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>{AGE_GROUPS.map((a) => <SelectItem key={a.value} value={a.value}>{label(a, lang)}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>{t("generator.language")}</Label>
              <Select value={language} onValueChange={setLanguage}>
                <SelectTrigger className="h-12 rounded-xl" data-testid="fam-lang"><SelectValue /></SelectTrigger>
                <SelectContent>{LANGUAGES.map((l) => <SelectItem key={l.value} value={l.value}>{label(l, lang)}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-2">
            <Label>{t("generator.theme")} <span className="text-secondary">*</span></Label>
            <Input value={theme} onChange={(e) => setTheme(e.target.value)} placeholder={t("generator.themePlaceholder")} className="h-12 rounded-xl" data-testid="fam-theme" />
            <div className="flex flex-wrap gap-2 pt-1">
              {THEMES.slice(0, 10).map((th, i) => (
                <button key={i} onClick={() => setTheme(label(th, lang))} className="rounded-full bg-muted hover:bg-muted/70 px-3 py-1 text-sm text-muted-foreground">{label(th, lang)}</button>
              ))}
            </div>
          </div>
          <Button onClick={submit} disabled={loading} className="w-full py-3 rounded-xl bg-secondary hover:bg-secondary/90 text-base" data-testid="fam-submit">
            {loading ? <><Loader2 className="h-5 w-5 animate-spin" /> {t("generator.generating")}</> : <><Home className="h-5 w-5" /> {t("common.generate")}</>}
          </Button>
        </div>
      </main>
    </div>
  );
}
